"use strict";

/* ------------------------------------------------------------------ *
 * Mapper 205: BMC-JC-016-2, the MMC3 pirate multicart
 * ------------------------------------------------------------------ *
 * The 15-in-1 and the 3-in-1 boards put a two-bit outer register in the
 * MMC3's PRG-RAM window and let each game use the MMC3 normally, except
 * that the MMC3 can only page *within* the block the register selects:
 *
 *   $6000-$7FFF   xxxx ..MM    M = Q0 (block bit 0) and Q1 (block bit 1)
 *
 * Two lines of hardware do it, and both are shared with mapper 37:
 *
 *   ROM A18 = Q1
 *   ROM A17 = Q0 or (MMC3 A17 and not Q1)
 *
 * applied to the PRG address, and the same pair on the CHR side. Written
 * out for the four values, next to the mask each one applies to the
 * MMC3's own page number, that is the table in the NESdev notes:
 *
 *   block  PRG-AND  PRG-OR  CHR-AND  CHR-OR
 *   0      $1F      $00     $FF      $000
 *   1      $1F      $10     $FF      $080
 *   2      $0F      $20     $7F      $100
 *   3      $0F      $30     $7F      $180
 *
 * so the whole mapper is that table applied to every bank number the MMC3
 * hands the cartridge -- the same translation the 37 and the 47 use, just
 * four rows of it instead of a formula. The masks and the ORs are also
 * what makes the fixed $C000/$E000 windows land on the last banks *of the
 * selected block* (63 & $1F | $00 = 31, 63 & $0F | $30 = 63), which is
 * how the menu and each game get their own reset vector.
 *
 * The board has no PRG RAM, so the window is the register and nothing
 * else, and since the MMC3 believes it is driving its RAM the $A001
 * write protect bit gates the write. The latch is not readable.
 *
 * The PCB also carries a solder pad that rewires block 1 into block 3,
 * which makes blocks 1 and 3 identical on a board with the pad set; the
 * 15-in-1's menu writes block 1 and looks at what appeared, to decide
 * which of its two menus to show. No dump needs the pad, so it is not
 * modelled and block 1 stays block 1.
 *
 * Used by the 15-in-1 and the 3-in-1 pirate multicarts.
 * See https://www.nesdev.org/wiki/INES_Mapper_205
 */

// The NESdev table, one row per block value. `and` masks the MMC3's page
// number down to the lines its block keeps, `or` supplies the rest.
var BLOCKS = [
  { prgAnd: 0x1f, prgOr: 0x00, chrAnd: 0xff, chrOr: 0x000 },
  { prgAnd: 0x1f, prgOr: 0x10, chrAnd: 0xff, chrOr: 0x080 },
  { prgAnd: 0x0f, prgOr: 0x20, chrAnd: 0x7f, chrOr: 0x100 },
  { prgAnd: 0x0f, prgOr: 0x30, chrAnd: 0x7f, chrOr: 0x180 },
];

function register(Mappers) {
  Mappers[205] = function (nes) {
    // The MMC3 constructor sets up command, bankSelect and the IRQ state.
    Mappers[4].call(this, nes);

    // Game mode / block select: QQ, 0-3.
    this.block = 0;
  };

  // Inherit from MMC3 (which inherits from NROM).
  Mappers[205].prototype = new Mappers[4]();

  Mappers[205].prototype.write = function (address, value) {
    if (address >= 0x6000 && address < 0x8000) {
      // The MMC3 sees this window as its PRG-RAM, so its write protect
      // ($A001 bit 1) gates the latch exactly as it would gate a RAM write.
      // Nothing is stored: there is no RAM here, and the latch is not
      // readable.
      if (this.prgRamWritable) {
        this.setBlock(value & 0x03);
      }
      return;
    }

    Mappers[4].prototype.write.apply(this, arguments);
  };

  Mappers[205].prototype.setBlock = function (value) {
    if (value === this.block) {
      return;
    }

    this.block = value;

    // Every window in both address spaces moved at once.
    this.updatePrgBanks();
    this.updateChrBanks();
  };

  // An 8 KB PRG bank number from the MMC3, translated into the ROM.
  Mappers[205].prototype.translatePrgBank = function (bank8k) {
    var block = BLOCKS[this.block];
    return (bank8k & block.prgAnd) | block.prgOr;
  };

  // A 1 KB CHR bank number from the MMC3, translated into the ROM.
  Mappers[205].prototype.translateChrBank = function (bank1k) {
    var block = BLOCKS[this.block];
    return (bank1k & block.chrAnd) | block.chrOr;
  };

  // Every cartridge window is loaded through these two helpers, so the
  // translation goes here rather than in a copy of updatePrgBanks() /
  // updateChrBanks(). The MMC3's loadCHRROM() uses loadVromBank() and is
  // deliberately not touched: it fills the pattern tables with the head of
  // the CHR ROM before any window is derived.
  Mappers[205].prototype.load8kRomBank = function (bank8k, address) {
    Mappers[4].prototype.load8kRomBank.call(
      this,
      this.translatePrgBank(bank8k),
      address,
    );
  };

  Mappers[205].prototype.load1kVromBank = function (bank1k, address) {
    Mappers[4].prototype.load1kVromBank.call(
      this,
      this.translateChrBank(bank1k),
      address,
    );
  };

  Mappers[205].prototype.reset = function () {
    Mappers[4].prototype.reset.apply(this);

    // The block select is cleared on the cartridge's reset line, so a soft
    // reset goes back to the multicart menu. The windows have to be
    // re-derived here: clearing the block without re-mapping would leave
    // the ROM windows pointing at the block the old value selected.
    this.block = 0;
    this.updatePrgBanks();
    this.updateChrBanks();
  };

  Mappers[205].prototype.toJSON = function () {
    var s = Mappers[4].prototype.toJSON.apply(this);
    s.block = this.block;
    return s;
  };

  Mappers[205].prototype.fromJSON = function (s) {
    // The block has to be back before the MMC3's restore re-derives the
    // windows from it, or they would be mapped for the old value.
    this.block = (s.block || 0) & 0x03;
    Mappers[4].prototype.fromJSON.apply(this, arguments);
  };
}

module.exports = { register: register };
